<template>
  <SearchApiEntityItemInput
    :search-result-options="searchResultOptions"
    :is-fetching-results="isFetchingResults"
    :placeholder="t('searchVehicleType')"
    @fetch="fetchResults"
    @update-selected-item="updateSelectedItem"
  />
</template>

<script lang="ts" setup>
import type { SearchResultOption } from '@/components/SearchApiEntityItemInput.vue';
import SearchApiEntityItemInput from '@/components/SearchApiEntityItemInput.vue';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVehicleTypesWithQuery } from '@/queries/use-vehicle-types.ts';
import { DecommissionedFilter } from '@/entities/cars/decommissioned-filter.enum.ts';
import MiniSearch from 'minisearch';
import type { VehicleType } from '@/entities/vehicle-type.entity.ts';

const emit = defineEmits<{
  (e: 'select', value: VehicleType): void;
}>();

const { t } = useI18n();

const miniSearch = new MiniSearch({
  fields: ['model', 'make.name'],
  extractField: (document, fieldName) => {
    if (fieldName === 'make.name') {
      return fieldName.split('.').reduce((doc, key) => doc && doc[key], document);
    }
    return document[fieldName];
  },
});

const { data: vehicleTypes, isFetching: isFetchingResults } = useVehicleTypesWithQuery({
  decommissioned: DecommissionedFilter.ACTIVE,
});

watch(
  vehicleTypes,
  () => {
    if (vehicleTypes.value) {
      miniSearch.addAll(vehicleTypes.value);
    }
  },
  { immediate: true },
);

const searchResultOptions = ref<SearchResultOption[]>([]);

const fetchResults = (search: string) => {
  const results = miniSearch.search(search, { fuzzy: 0.3 });
  results.sort((a, b) => b.score - a.score).splice(10);
  searchResultOptions.value.splice(0, searchResultOptions.value.length);
  results.forEach((item) => {
    const vehicleType = vehicleTypes.value?.find((v) => v.id === item.id);
    if (vehicleType) {
      searchResultOptions.value.push({
        item: vehicleType,
        content: [vehicleType.make.name + ' ' + vehicleType.model],
      });
    }
  });
};

const updateSelectedItem = (item: VehicleType) => {
  emit('select', item);
};
</script>

<i18n lang="json">
{
  "en": {
    "searchVehicleType": "Search vehicle type"
  },
  "de": {
    "searchVehicleType": "Vehicle Type suchen"
  },
  "es": {
    "searchVehicleType": "Buscar tipo de vehículo"
  }
}
</i18n>
